import { dayjs } from '@mst-fe/shared/dayjs';
import { TableProps } from 'antd';
import { SortOrder, SorterResult } from 'antd/lib/table/interface';
import get from 'lodash/get';
import omit from 'lodash/omit';

import { defaultSort } from '../../../../../../utils/tables';
import { DataQueryFormItemMetaTableConfig, DataQueryTableConfigSorting } from '../../../../types';

export const DEFAULT_PAGE_SIZE = 20;

export type SorterFactory = (
  dataIndex: string,
  isDate?: boolean
) => { sorter: (a: object, b: object) => number; sortOrder: SortOrder | undefined };

export const createSorterFactory =
  (currentConfig?: DataQueryTableConfigSorting) =>
  (tableName: string): SorterFactory =>
  (dataIndex, isDate) => {
    const sortOrder = currentConfig?.[tableName]?.column === dataIndex ? currentConfig?.[tableName]?.order : undefined;

    return {
      sorter: (a: object, b: object) => {
        const lhs = get(a, dataIndex);
        const rhs = get(b, dataIndex);
        if (isDate && dayjs(lhs).isValid()) {
          return dayjs(lhs).diff(rhs);
        } else if (!isDate && (typeof lhs === 'string' || lhs instanceof String) && (typeof rhs === 'string' || rhs instanceof String)) {
          return lhs.localeCompare(rhs as string);
        }
        return defaultSort(lhs, rhs);
      },
      sortOrder,
    };
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SorterCallback = (tableName: string) => TableProps<any>['onChange'];

export const createSorterCallback =
  (onTableConfigChanged?: (fb: (value: DataQueryFormItemMetaTableConfig) => DataQueryFormItemMetaTableConfig) => void): SorterCallback =>
  (tableName) =>
  (pagination, filter, sort) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { order, column: { dataIndex } = {} } = sort as SorterResult<any>;

    if (!onTableConfigChanged) {
      return;
    }
    if (!dataIndex) {
      onTableConfigChanged((c) => ({
        ...c,
        sorting: omit(c?.sorting || {}, [tableName]),
      }));
    } else {
      onTableConfigChanged((c) => ({
        ...c,
        sorting: {
          ...c?.sorting,
          [tableName]: {
            column: dataIndex,
            order,
          },
        },
      }));
    }
  };
